import { GeoSearch, ToolkitProperty } from '@aloft/toolkit-shared';

import { useState } from 'react';
import { SubmitErrorHandler, SubmitHandler, useFieldArray } from 'react-hook-form';

import { useToaster } from '../../../hooks/useToaster/useToaster.tsx';
import { schNewFormPair } from '../schemas.ts';
import { InitialStudy, PairedSalesForm, PairedSalesFormInput, StudyOutput } from '../types.ts';
import { usePairedSalesForm } from './usePairedSalesForm.ts';

export const usePairedSalesInternalState = (
  initialStudy: InitialStudy,
  onSave: (study: StudyOutput) => Promise<void>,
) => {
  const [activePair, setActivePair] = useState<number | null>(
    initialStudy?.body?.selectedPairs ? 0 : null,
  );

  const makeToast = useToaster();

  const form = usePairedSalesForm(initialStudy.body);

  const { append, remove } = useFieldArray({
    control: form.control,
    name: 'selectedPairs',
  });

  const onInvalid: SubmitErrorHandler<PairedSalesFormInput> = (errors) => {
    console.warn(errors);
    makeToast({
      type: 'error',
      id: 'submit-study-error',
      title: 'Error creating study',
      body: 'There are one or more errors with your study. Please ensure you entered a reconciled value and do not have any errors in your grid annotations',
    });
  };

  const onSubmit: SubmitHandler<PairedSalesForm> = async (data) => {
    const study: StudyOutput = {
      input: initialStudy.input,
      body: {
        notes: data.notes ?? '',
        reconciledValue: data.reconciledValue,
        selectedPairs: data.selectedPairs,
        search: {
          filters: data.search.filters,
          pairCriteria: data.search.pairCriteria,
          geoSearch: data.search.geoSearch,
        },
      },
    };

    await onSave(study);
  };

  const selectPair = (p1: ToolkitProperty, p2: ToolkitProperty) => {
    append(schNewFormPair.parse({ p1, p2, adjustments: {} }));
    setActivePair(form.getValues('selectedPairs').length - 1);
  };

  const deselectPair = (p1: ToolkitProperty, p2: ToolkitProperty) => {
    const currentPairs = form.getValues('selectedPairs');

    const indexToRemove = currentPairs.findIndex(
      (x) =>
        x.p1.toolkitRecordID === p1.toolkitRecordID && x.p2.toolkitRecordID === p2.toolkitRecordID,
    );

    remove(indexToRemove);

    setActivePair((oldActivePair) => {
      if (currentPairs.length === 1) return null;
      if (oldActivePair && oldActivePair > 0 && indexToRemove <= oldActivePair) {
        return oldActivePair - 1;
      }

      return oldActivePair;
    });
  };

  const handleSwapProperties = () => {
    if (activePair === null) return;
    const pair = form.getValues('selectedPairs')[activePair];

    if (pair === undefined) return;

    form.setValue(`selectedPairs.${activePair}`, {
      p1: pair.p2,
      p2: pair.p1,
      adjustments: pair.adjustments,
    });

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    form.trigger(`selectedPairs.${activePair}`);
  };

  // TODO: Remove radius search types then remove this getter and setter
  const getGeoSearch = () => {
    const geoSearch = form.getValues('search.geoSearch');
    if (geoSearch.t !== 'polygon') {
      throw new Error('Radius search unsupported');
    }
    return geoSearch;
  };

  const setGeoSearch = (geoSearch: GeoSearch) => {
    form.setValue('search.geoSearch', geoSearch);
  };

  return {
    form,
    onSubmit,
    onInvalid,

    selectPair,
    deselectPair,

    activePair,
    setActivePair,

    setGeoSearch,
    getGeoSearch,

    handleSwapProperties,
  };
};
